"use client";

import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "convex/react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { TiptapEditor } from "./tiptap-editor";
import { GenericImageUpload } from "@/components/ui/generic-image-upload";
import { ImageDisplay } from "@/components/ui/image-display";
import { SimpleLoader } from "@/components/ui/simple-loader";
import { X, Save, Eye } from "lucide-react";

// helper function to strip HTML tags for validation
const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

const coverImageSchema = z
  .object({
    key: z.string(),
    filename: z.string(),
    contentType: z.string().optional(),
    size: z.number().optional(),
  })
  .nullable();

const blogFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  slug: z
    .string()
    .min(1, "Slug is required")
    .regex(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      "Slug must be URL-friendly (lowercase letters, numbers, and hyphens only)"
    ),
  metaDescription: z.string().optional(),
  content: z
    .string()
    .min(1, "Content is required")
    .refine(
      (val) => stripHtml(val).trim().length > 0,
      "Content must have meaningful text"
    ),
  tags: z.array(z.string()),
  published: z.boolean(),
  coverImage: coverImageSchema.nullable(),
});

type BlogFormValues = z.infer<typeof blogFormSchema>;

interface BlogFormProps {
  blogId?: Id<"blog">;
  onClose: () => void;
  onSuccess: () => void;
}

export function BlogForm({ blogId, onClose, onSuccess }: BlogFormProps) {
  const [tagInput, setTagInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // queries
  const existingBlog = useQuery(
    api.blog.getBlogPostById,
    blogId ? { id: blogId } : "skip"
  );

  // mutations
  const createBlog = useMutation(api.blog.createBlogPost);
  const updateBlog = useMutation(api.blog.updateBlogPost);

  const form = useForm<BlogFormValues>({
    resolver: zodResolver(blogFormSchema),
    defaultValues: {
      title: "",
      slug: "",
      metaDescription: "",
      content: "",
      tags: [] as string[],
      published: false,
      coverImage: null,
    },
    mode: "onChange",
  });

  // load existing blog data
  useEffect(() => {
    if (existingBlog) {
      form.reset({
        title: existingBlog.title,
        slug: existingBlog.slug,
        metaDescription: existingBlog.meta_description || "",
        content: existingBlog.content,
        tags: existingBlog.tags,
        published: existingBlog.published || false,
        coverImage: existingBlog.cover_image || null,
      });
    }
  }, [existingBlog, form]);

  // auto-generate slug from title
  useEffect(() => {
    const title = form.watch("title");
    const slug = form.watch("slug");
    if (title && !slug) {
      const generatedSlug = title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .trim();
      form.setValue("slug", generatedSlug);
    }
  }, [form.watch("title"), form.watch("slug"), form]);

  // handle tag input
  const handleTagInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag();
    }
  };

  const addTag = () => {
    const newTag = tagInput.trim();
    const currentTags = form.getValues("tags");
    if (newTag && !currentTags.includes(newTag)) {
      form.setValue("tags", [...currentTags, newTag]);
      setTagInput("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    const currentTags = form.getValues("tags");
    form.setValue(
      "tags",
      currentTags.filter((tag) => tag !== tagToRemove)
    );
  };

  // extract and sanitize error message from Convex errors
  const extractErrorMessage = (error: unknown): string => {
    if (!error) {
      return "An unknown error occurred";
    }

    let rawMessage = "";
    let errorString = "";

    // handle Error instances
    if (error instanceof Error) {
      rawMessage = error.message || "";
      errorString = error.toString();
    } else if (typeof error === "string") {
      rawMessage = error;
      errorString = error;
    } else if (typeof error === "object" && error !== null) {
      const errorObj = error as Record<string, unknown>;
      if (typeof errorObj.message === "string") {
        rawMessage = errorObj.message;
      } else if (typeof errorObj.error === "string") {
        rawMessage = errorObj.error;
      } else {
        try {
          errorString = JSON.stringify(errorObj);
        } catch {
          errorString = String(error);
        }
      }
    } else {
      errorString = String(error);
    }

    // combine all possible error sources
    const fullErrorText = `${rawMessage}\n${errorString}`.trim();

    // extract the actual error message from Convex error format
    // format: "[CONVEX M(...)] ... Server Error\n\nUncaught Error: Actual message"
    const patterns = [
      // pattern 1: "Uncaught Error: message"
      /Uncaught Error:\s*([^\n]+?)(?:\s+at\s+handler|\s+Called by|\n|$)/s,
      // pattern 2: extract after "Server Error" if present
      /Server Error[\s\n]+(.+?)(?:\s+at\s+handler|\s+Called by|\n|$)/s,
      // pattern 3: look for error message after removing metadata
      /(?:\[CONVEX.*?\]\s*)*\[Request ID:.*?\]\s*Server Error[\s\n]+(.+?)(?:\s+at\s+handler|\s+Called by|\n|$)/s,
    ];

    for (const pattern of patterns) {
      const match = fullErrorText.match(pattern);
      if (match && match[1]) {
        let extracted = match[1].trim();
        // clean up the extracted message
        extracted = extracted
          .replace(/\[CONVEX\s+M\([^)]+\)\]/g, "")
          .replace(/\[Request ID:\s*[^\]]+\]/g, "")
          .replace(/Server Error/gi, "")
          .replace(/Uncaught Error:\s*/gi, "")
          .replace(/\s+/g, " ")
          .trim();

        if (extracted && extracted.length > 0) {
          return extracted;
        }
      }
    }

    // if no pattern matched, clean up the raw message
    let cleaned = rawMessage || errorString;
    cleaned = cleaned
      .replace(/\[CONVEX\s+M\([^)]+\)\]/g, "")
      .replace(/\[Request ID:\s*[^\]]+\]/g, "")
      .replace(/Server Error/gi, "")
      .replace(/Uncaught Error:\s*/gi, "")
      .replace(/\s+/g, " ")
      .trim();

    if (cleaned && cleaned.length > 0 && cleaned !== "{}") {
      return cleaned;
    }

    return "Failed to save blog post";
  };

  // handle form submission
  const onSubmit = async (values: BlogFormValues) => {
    setIsSubmitting(true);

    try {
      if (blogId) {
        // update existing blog
        await updateBlog({
          id: blogId,
          title: values.title.trim(),
          slug: values.slug.trim(),
          content: values.content, // don't trim content - preserve whitespace
          meta_description: values.metaDescription?.trim() || undefined,
          cover_image: values.coverImage || undefined,
          tags: values.tags,
          published: values.published,
          updated_by: "blogmaster@blirumahproperty.com", // this will be replaced with actual user
        });
        toast.success("Blog post updated successfully");
      } else {
        // create new blog
        await createBlog({
          title: values.title.trim(),
          slug: values.slug.trim(),
          content: values.content, // don't trim content - preserve whitespace
          meta_description: values.metaDescription?.trim() || undefined,
          cover_image: values.coverImage || undefined,
          tags: values.tags,
          published: values.published,
          created_by: "blogmaster@blirumahproperty.com", // this will be replaced with actual user
        });
        toast.success("Blog post created successfully");
      }
      onSuccess();
    } catch (error) {
      console.error("Error saving blog:", error);
      const errorMessage = extractErrorMessage(error);
      console.log("Extracted error message:", errorMessage);
      toast.error(errorMessage, {
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (blogId && !existingBlog) {
    return (
      <div className="flex items-center justify-center p-8">
        <SimpleLoader message="Loading blog post..." />
      </div>
    );
  }

  const tags = form.watch("tags");
  const coverImage = form.watch("coverImage");

  return (
    <Form {...form}>
      <DialogHeader className="contents space-y-0 text-left">
        <ScrollArea className="flex max-h-full flex-col overflow-hidden">
          <DialogTitle className="px-6 pt-6">
            {blogId ? "Edit Blog Post" : "Create New Blog Post"}
          </DialogTitle>
          <div className="px-6 pb-2">
            <p className="text-muted-foreground">
              {blogId
                ? "Update your blog post"
                : "Write and publish a new blog post"}
            </p>
          </div>
          <div className="px-6 pb-6">
            <form
              id="blog-form"
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-6"
            >
              {/* basic info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Basic Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title *</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Enter blog post title"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="slug"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Slug *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="blog-post-url-slug" />
                        </FormControl>
                        <FormDescription>
                          URL-friendly version of the title. Used in the blog
                          post URL.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="metaDescription"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Meta Description</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder="Brief description for SEO and social media"
                            rows={3}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* cover image */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Cover Image</CardTitle>
                </CardHeader>
                <CardContent className="overflow-hidden">
                  <FormField
                    control={form.control}
                    name="coverImage"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormControl>
                          {field.value ? (
                            <div className="space-y-4 w-full">
                              <div className="relative w-full h-48 rounded-lg overflow-hidden border">
                                <ImageDisplay
                                  imageKey={field.value.key}
                                  alt={field.value.filename}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="flex items-center justify-between gap-2 min-w-0 w-full max-w-full">
                                <div
                                  className="text-sm text-muted-foreground min-w-0 max-w-[256px] overflow-x-auto whitespace-nowrap [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                                  style={{
                                    mask: "linear-gradient(to right, #0000, #ffff var(--left-fade) calc(100% - var(--right-fade)), #0000)",
                                    animation: "scrollfade",
                                    animationTimeline: "--scrollfade",
                                    scrollTimeline: "--scrollfade x",
                                  }}
                                >
                                  {field.value.filename}
                                </div>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => field.onChange(null)}
                                  className="shrink-0"
                                >
                                  Remove
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <GenericImageUpload
                              value={null}
                              onChange={(imageData) =>
                                field.onChange(imageData)
                              }
                              uploadPath="blog"
                              imageType="cover"
                              aspectRatio="aspect-[16/9]"
                              disabled={false}
                              className="w-full h-full"
                            />
                          )}
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* content */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Content</CardTitle>
                </CardHeader>
                <CardContent>
                  <FormField
                    control={form.control}
                    name="content"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <TiptapEditor
                            content={field.value}
                            onChange={field.onChange}
                            placeholder="Write your blog post content here..."
                            minHeight="400px"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* tags */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Tags</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="tag-input">Add Tags</Label>
                    <Input
                      id="tag-input"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={handleTagInput}
                      placeholder="Type a tag and press Enter or comma"
                    />
                    <p className="text-sm text-muted-foreground">
                      Press Enter or comma to add a tag
                    </p>
                  </div>

                  {tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag) => (
                        <Badge key={tag} variant="secondary" className="gap-1">
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="ml-1 hover:text-destructive"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* publish options */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Publish Options</CardTitle>
                </CardHeader>
                <CardContent>
                  <FormField
                    control={form.control}
                    name="published"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center space-x-2">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <FormLabel className="flex items-center gap-2">
                            <Eye className="h-4 w-4" />
                            Publish this blog post
                          </FormLabel>
                        </div>
                        <FormDescription>
                          Published posts are visible to the public. Unpublished
                          posts are saved as drafts.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </form>
          </div>
        </ScrollArea>
      </DialogHeader>
      <DialogFooter className="flex-row items-center justify-end border-t px-6 py-4">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="blog-form"
          disabled={isSubmitting || !form.formState.isValid}
          className="gap-2"
        >
          {isSubmitting ? (
            <SimpleLoader message="Saving..." />
          ) : (
            <>
              <Save className="h-4 w-4" />
              {blogId ? "Update Blog Post" : "Create Blog Post"}
            </>
          )}
        </Button>
      </DialogFooter>
    </Form>
  );
}
